import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useSignContract, useUser } from '~/api/user';
import { Button, Dialog } from '~/components';
import { Checkbox, Field, Form } from '~/components/Form';
import { toast } from '~/utils/toast';

import type { CompletionTabProps as Props } from './types';

import { formStackStyle } from '../CompleteAccountDialog.css';
import { FrameworkAgreement } from '../FrameworkAgreement/FrameworkAgreement';

export const FrameworkAgreementTab = ({ isLastTab, onCompleted }: Props) => {
  const { data: user } = useUser();
  const mutation = useSignContract();

  const userNeedsToSignAddendum = user.contractSigned && !user.addendumSigned;
  const hasSigned = user.contractSigned && user.addendumSigned;

  const { control, handleSubmit } = useForm<{ agreed: boolean }>({
    defaultValues: { agreed: hasSigned },
  });

  const { t } = useTranslation(['dashboard', 'settings']);

  const onSubmit = handleSubmit(() => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.success({ description: t('settings:alerts.successUpdated') });
        onCompleted();
      },
      onError: () => toast.danger({ description: t('settings:accountCompletionModal.frameworkAgreement.error') }),
    });
  });

  return (
    <>
      <FrameworkAgreement hasSigned={hasSigned} isAddendum={userNeedsToSignAddendum} />

      <Form className={formStackStyle} onSubmit={onSubmit}>
        <Controller
          control={control}
          name="agreed"
          render={({ field: { name, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root
              dirty={isDirty}
              disabled={hasSigned || mutation.isPending || mutation.isSuccess}
              invalid={invalid}
              name={name}
              touched={isTouched}
            >
              <Checkbox.Root checked={value} name={name} onCheckedChange={onChange}>
                <Checkbox.Label>{t('dashboard:frameworkAgreement.checkboxLabel')}</Checkbox.Label>
              </Checkbox.Root>

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{ required: { value: true, message: t('dashboard:frameworkAgreement.checkboxError') } }}
        />

        <Dialog.Actions>
          {hasSigned ? (
            <Button onClick={onCompleted} type="button">
              {t(`settings:accountCompletionModal.action.${isLastTab ? 'complete' : 'next'}`)}
            </Button>
          ) : (
            <Button loading={mutation.isPending} type="submit">
              {t(`settings:accountCompletionModal.action.${isLastTab ? 'complete' : 'save'}`)}
            </Button>
          )}
        </Dialog.Actions>
      </Form>
    </>
  );
};
